import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from '../cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Inclina o botao como o logotipo. Por defeito so o `primary` inclina. */
  slanted?: boolean;
  /** Simbolo depois do texto, como as setas de browser.growup.gg (→ ↗ ↘). */
  trailing?: ReactNode;
};

const base =
  'inline-flex items-center justify-center gap-2 font-sub font-bold uppercase tracking-[var(--gu-btn-tracking)] ' +
  'transition-colors duration-150 ease-snap focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-gu-focus focus-visible:ring-offset-2 focus-visible:ring-offset-gu-ground ' +
  'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-gu-accent text-gu-on-accent hover:bg-gu-accent-hover',
  secondary: 'border-2 border-gu-ink text-gu-ink hover:bg-gu-ink hover:text-gu-ground',
  ghost: 'text-gu-ink hover:bg-gu-ink/10',
  // So no back-office: acoes que nao se desfazem (bloquear PC, apagar).
  danger: 'border-2 border-gu-danger text-gu-danger hover:bg-gu-danger hover:text-gu-ground',
};

const sizes: Record<ButtonSize, string> = {
  // Medidas no tokens.json (secao `medidas`, btn-*). As alturas de linha sao as de antes.
  sm: 'min-h-[var(--gu-btn-sm-h)] px-[var(--gu-btn-sm-px)] text-[length:var(--gu-btn-sm-text)]',
  md: 'min-h-[var(--gu-btn-md-h)] px-[var(--gu-btn-md-px)] text-[length:var(--gu-btn-md-text)] leading-4',
  lg: 'min-h-[var(--gu-btn-lg-h)] px-[var(--gu-btn-lg-px)] text-[length:var(--gu-btn-lg-text)] leading-5',
};

export function buttonClass({
  variant = 'primary',
  size = 'md',
  slanted,
}: Pick<CommonProps, 'variant' | 'size' | 'slanted'> = {}): string {
  const slant = slanted ?? variant === 'primary';
  return cx(base, variants[variant], sizes[size], slant && 'gu-slant');
}

function Content({
  children,
  trailing,
  slanted,
}: {
  children: ReactNode;
  trailing?: ReactNode;
  slanted: boolean;
}) {
  const inner = (
    <>
      {children}
      {trailing ? <span aria-hidden="true">{trailing}</span> : null}
    </>
  );
  return slanted ? (
    <span className="gu-unslant inline-flex items-center gap-2">{inner}</span>
  ) : (
    inner
  );
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & CommonProps;

export function Button({
  variant = 'primary',
  size = 'md',
  slanted,
  trailing,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  const slant = slanted ?? variant === 'primary';
  return (
    <button
      type={type}
      className={cx(buttonClass({ variant, size, slanted: slant }), className)}
      {...props}
    >
      <Content slanted={slant} trailing={trailing}>
        {children}
      </Content>
    </button>
  );
}

export type ButtonLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & CommonProps;

/** O mesmo visual que `Button`, para navegacao (um link e um link, nao um botao). */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  slanted,
  trailing,
  className,
  children,
  ...props
}: ButtonLinkProps) {
  const slant = slanted ?? variant === 'primary';
  return (
    <a className={cx(buttonClass({ variant, size, slanted: slant }), className)} {...props}>
      <Content slanted={slant} trailing={trailing}>
        {children}
      </Content>
    </a>
  );
}
